import { QueryClient, QueryFunction } from "@tanstack/react-query";

async function throwIfResNotOk(res: Response) {
  if (!res.ok) {
    const text = (await res.text()) || res.statusText;
    throw new Error(`${res.status}: ${text}`);
  }
}

// Get the org-scoped API path based on current context
export function getApiPath(path: string): string {
  // If path already includes /org/, don't modify it
  if (path.startsWith('/org/')) {
    return path;
  }
  
  // Skip org scoping for global routes (super-admin only)
  // Note: QuickBooks and Gmail are org-scoped, not global
  const globalPaths = ['/api/auth', '/api/super-admin'];
  if (globalPaths.some(globalPath => path.startsWith(globalPath))) {
    return path;
  }
  
  // Check for org slug in URL first (for org-scoped pages)
  const urlMatch = window.location.pathname.match(/^\/org\/([^/]+)/);
  if (urlMatch) {
    const orgSlug = urlMatch[1];
    // Convert /api/... to /org/{slug}/api/...
    if (path.startsWith('/api/')) {
      return `/org/${orgSlug}${path}`;
    }
  }
  
  // Fallback to session storage
  const orgSlug = sessionStorage.getItem('currentOrgSlug');
  if (orgSlug && path.startsWith('/api/')) {
    return `/org/${orgSlug}${path}`;
  }
  
  // Default: return path unchanged
  return path;
}

export async function apiRequest(
  method: string,
  url: string,
  data?: unknown | undefined,
): Promise<Response> {
  const scopedUrl = getApiPath(url);
  const res = await fetch(scopedUrl, {
    method,
    headers: data ? { "Content-Type": "application/json" } : {},
    body: data ? JSON.stringify(data) : undefined,
    credentials: "include",
  });

  await throwIfResNotOk(res);
  return res;
}

type UnauthorizedBehavior = "returnNull" | "throw";
export const getQueryFn: <T>(options: {
  on401: UnauthorizedBehavior;
}) => QueryFunction<T> =
  ({ on401: unauthorizedBehavior }) =>
  async ({ queryKey }) => {
    const url = queryKey.join("/") as string;
    const scopedUrl = getApiPath(url);
    const res = await fetch(scopedUrl, {
      credentials: "include",
      redirect: "manual", // Prevent automatic redirect following
    });

    // Block automatic redirects from navigating the browser
    if (res.type === "opaqueredirect" || res.redirected) {
      throw new Error("API returned redirect - authentication may be required");
    }

    if (unauthorizedBehavior === "returnNull" && res.status === 401) {
      return null;
    }

    await throwIfResNotOk(res);
    return await res.json();
  };

// A read cached under a key that names the org: ["/api/categories", orgSlug]. Such a
// key no longer joins into a URL, so the read names its path, fetched with the default
// query function's behaviour (getApiPath scoping, `${status}: ${body}` errors).
// A bare "/api/..." key is one cache entry for every vendor a buyer opens in one app
// session, and staleTime is Infinity: org `demo`'s categories and market report showed
// on Pitt's page (2026-10-24), as a custom-URL account did at the wrong vendor (#260).
//
// Never spread the query context (`{ ...ctx }`): the spread reads `ctx.signal`, and a
// query whose function read its signal is CANCELLED and REVERTED to its old data when
// its last observer unmounts mid-fetch. A submit invalidates orders/current and
// navigates away in the same tick, so the refetch was dropped and Back from the
// summary seeded the cart with the order as it was before that submit — the next
// submit replaced the order with those lines (Gomes, 2026-10-25/26). Pinned by
// tests/client/submit-refetch-survives-unmount.test.ts.
export function readPath<T>(path: string): QueryFunction<T> {
  return (ctx) =>
    getQueryFn<T>({ on401: "throw" })({
      client: ctx.client,
      queryKey: [path],
      meta: ctx.meta,
    } as unknown as Parameters<QueryFunction<T>>[0]);
}

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      queryFn: getQueryFn({ on401: "throw" }),
      refetchInterval: false,
      refetchOnWindowFocus: false,
      staleTime: Infinity,
      retry: false,
    },
    mutations: {
      retry: false,
    },
  },
});
